import { Button, Checkbox, message, Space, Table, Typography } from "antd"
import React, { useEffect, useState } from "react"
import ComplaintDrawer from "../../../components/Orders/ComplaintDrawer"
import moment from "moment"
import { CheckSquareFilled, CloseOutlined, FileOutlined } from "@ant-design/icons"
import "./WarehouseOrders.css"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
// import MarkAsDone from "../../../components/Orders/Helpers/MarkAsDone"
import { endDate, startDate } from "../../../components/Orders/Helpers/TableDetails"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"
import OrderCountDisplay from "../../../components/Table/orderCountDisplay"
import AddCustomInvoice from "../../../components/Orders/Helpers/AddCustomInvoice"
import MarkAsDone from "../../../components/Orders/Helpers/MarkAsDone"
import AddCustomPackingList from "../../../components/Orders/Helpers/AddCustomPackingList"
import { useAuthContext } from "../../../hooks/useAuthContext"
import { filterOrdersByRole } from "../../../utils/filterOrdersByRole"

const { Text, Link } = Typography

export default function WarehouseOrders({ initialData, loading }) {
  const [data, setData] = useState(initialData)
  const { user, admin, semiAdmin, devSpecialist } = useAuthContext()

  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const selectedOrder = selectedRows.length === 1 ? selectedRows[0] : null
  const exactlyOneSelected = selectedRows.length === 1

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, rows) => { setSelectedRowKeys(keys); setSelectedRows(rows) },
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
  }

  const [filterValues, setFilterValues] = useState({
    orderNumber: true,
    machine: true,
    dueDate: true,
  })

  const handleInvoiceDownload = async (recordId) => {
    try {
      const docRef = doc(db, "orders", recordId)
      await updateDoc(docRef, {
        invoiceDownloaded: true,
      })
    } catch (error) {
      console.error("Error updating Firestore document: ", error)
    }
  }
  const handleProformaDownload = async (recordId) => {
    try {
      const docRef = doc(db, "orders", recordId)
      await updateDoc(docRef, {
        proformaDownloaded: true,
      })
    } catch (error) {
      console.error("Error updating Firestore document: ", error)
    }
  }

  const handlePackingListDownload = async (recordId) => {
    try {
      const docRef = doc(db, "orders", recordId)
      await updateDoc(docRef, {
        packingListDownloaded: true,
      })
    } catch (error) {
      console.error("Error updating Firestore document: ", error)
    }
  }

  const handleCustomPackingListDownload = async (recordId) => {
    try {
      const docRef = doc(db, "orders", recordId)
      await updateDoc(docRef, {
        "customPackingListData.downloaded": true, // Use dot notation
      })
      // Update local state
      setData((prevData) =>
        prevData.map((item) =>
          item.id === recordId && item.customPackingListData
            ? {
                ...item,
                customPackingListData: { ...item.customPackingListData, downloaded: true },
              }
            : item,
        ),
      )
    } catch (error) {
      console.error("Error updating Firestore document (custom packing list): ", error)
      message.error("Failed to mark custom packing list as downloaded.")
    }
  }

  useEffect(() => {
    setTimeout(() => {
      const filtered = filterOrdersByRole(initialData, { admin, semiAdmin, devSpecialist })
      setData(filtered)
    }, 200)
  }, [initialData, admin, semiAdmin, devSpecialist])

  //client filter
  let clientFIlters =
    data &&
    data.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  let productFilters =
    data &&
    data.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  const returnCorrectButton = (record) => {
    // Define flags for clarity, using nullish coalescing for safety
    const hasInvoice = record?.hasInvoice ?? false
    const hasStdPackingList = record?.hasPackingList ?? false
    const hasCustomPackingList = record?.customPackingList ?? false
    const hasProforma = record?.hasProforma ?? false

    // Check for data integrity for conditional rendering
    const invoiceUrl = record?.invoice?.invoiceUrl
    const stdPackingListUrl = record?.packingListUrl
    const proformaUrl = record?.proforma?.proformaUrl
    const customPLData = record?.customPackingListData
    const customPLUrl = customPLData?.documentDownloadUrl

    return (
      <Space direction="vertical" align="center" size={4}>
        {/* --- Standard Invoice (Show if exists) --- */}
        {hasInvoice && invoiceUrl && (
          <a
            href={invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleInvoiceDownload(record.id)}>
            <Button size="small" icon={<FileOutlined style={{ color: "#1677ff" }} />}>
              {record.invoice?.name || "Invoice"}
              {record.invoiceDownloaded && (
                <CheckSquareFilled style={{ marginLeft: 4, color: "#1677ff" }} />
              )}
            </Button>
          </a>
        )}
        {/* --- Standard Packing List (Show if exists) --- */}
        {hasStdPackingList && stdPackingListUrl && (
          <a
            href={stdPackingListUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handlePackingListDownload(record.id)}>
            <Button size="small" icon={<FileOutlined style={{ color: "#52c41a" }} />}>
              Packing List
              {record.packingListDownloaded && (
                <CheckSquareFilled style={{ marginLeft: 4, color: "#1677ff" }} />
              )}
            </Button>
          </a>
        )}
        {/* --- Proforma (Show ONLY if NO standard invoice/PL exist) --- */}
        {!hasInvoice && !hasStdPackingList && hasProforma && proformaUrl && (
          <a
            href={proformaUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleProformaDownload(record.id)}>
            <Button size="small" icon={<FileOutlined style={{ color: "#1677ff" }} />}>
              {record.proforma?.name || "Proforma"}
              {record.proformaDownloaded && (
                <CheckSquareFilled style={{ marginLeft: 4, color: "#1677ff" }} />
              )}
            </Button>
          </a>
        )}
        {/* --- "No Document" Text (Show ONLY if NO docs of any kind exist yet) --- */}
        {!hasInvoice && !hasStdPackingList && !hasProforma && !hasCustomPackingList && (
          <Text type="secondary" style={{ paddingLeft: "4px" }}>
            No Document
          </Text>
        )}
        {/* --- Custom Packing List Section (ALWAYS RENDER: Link or Upload Button) --- */}
        {hasCustomPackingList && customPLUrl ? (
          // Show link to EXISTING custom packing list
          <a
            href={customPLUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleCustomPackingListDownload(record.id)}>
            <Button size="small" icon={<FileOutlined style={{ color: "#faad14" }} />}>
              {customPLData?.name || "Custom PL"}
              {customPLData?.downloaded && (
                <CheckSquareFilled style={{ marginLeft: 4, color: "#1677ff" }} />
              )}
            </Button>
          </a>
        ) : (
          // Show button to ADD a new custom packing list
          <AddCustomPackingList record={record} />
        )}
        {/* --- Add Custom Invoice Button (Show ONLY if NO standard invoice/PL exist) --- */}
        {!hasInvoice && !hasStdPackingList && <AddCustomInvoice record={record} />}
      </Space>
    )
  }

  const markOrderAsDone = (orderId) => {
    // Trigger the fade-out animation

    // Wait for the animation to complete before removing the item
    setTimeout(() => {
      setData((prevData) => prevData.filter((item) => item.id !== orderId))
    }, 1000) // Ensure this matches the CSS animation duration
  }

  //filter content
  const returnFilterContent = () => {
    // Handler to update filter values
    const handleCheckboxChange = (column, checked) => {
      setFilterValues((prevValues) => ({
        ...prevValues,
        [column]: checked,
      }))
    }

    return (
      <div>
        <Space direction="vertical">
          <Checkbox
            onChange={(e) => handleCheckboxChange("orderNumber", e.target.checked)}
            checked={filterValues.orderNumber}>
            Order Number
          </Checkbox>
          <Checkbox
            onChange={(e) => handleCheckboxChange("machine", e.target.checked)}
            checked={filterValues.machine}>
            Machine
          </Checkbox>
          <Checkbox
            onChange={(e) => handleCheckboxChange("dueDate", e.target.checked)}
            checked={filterValues.dueDate}>
            Due Date
          </Checkbox>
        </Space>
      </div>
    )
  }

  //default columns for table
  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      width: "10%",
      render: (value, record) => OrderAndPrefix(record),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      width: "15%",
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
      ellipsis: true,
      render: (value) => {
        return (
          <>
            <Text copyable={{ text: value }}>{value}</Text>
          </>
        )
      },
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      width: "25%",
      render: (value) => {
        return (
          <>
            <Text copyable={{ text: value }}>{value}</Text>
          </>
        )
      },
    },
    {
      title: "Manufacturer",
      dataIndex: ["manufacturer", "company"],
      align: "center",
      ellipsis: true,
    },
    {
      title: "Box count",
      dataIndex: "boxesCount",
      align: "center",
      render: (value, record) => {
        if (record.selectedProduct.machine === "Tape") {
          return (
            <>
              <Text>{record.boxCount}</Text>
            </>
          )
        } else {
          return (
            <>
              <Text>{value}</Text>
            </>
          )
        }
      },
    },
    {
      title: "Count",
      dataIndex: "amountCreated",
      align: "center",
      render: (value, record) => <OrderCountDisplay value={value} record={record} />,
    },
    {
      title: "Document",
      align: "center",
      width: "10%",
      render: (value, record) => {
        return returnCorrectButton(record) // Add the return keyword here
      },
    },
    {
      title: "Technical",
      dataIndex: "technicalPage",
      align: "center",
      render: (value) => {
        if (value) {
          return (
            <Link target="_blank" href={value}>
              Link
            </Link>
          )
        } else {
          return <CloseOutlined />
        }
      },
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
    },
    {
      title: "Started",
      dataIndex: "created",
      align: "center",
      sorter: (a, b) => a.created.seconds - b.created.seconds,
      render: (value) => startDate(value),
    },
    {
      title: "Ended",
      align: "center",
      dataIndex: "doneByRewinder",
      sorter: (a, b) => a.doneByRewinder.seconds - b.doneByRewinder.seconds,
      render: (value, record) => endDate(value, record),
    },
    {
      title: "Due date",
      dataIndex: "orderDueDate",
      align: "center",
      sorter: (a, b) => a.orderDueDate.seconds - b.orderDueDate.seconds,
      render: (value) => <Text>{moment.unix(value.seconds).format("DD.MM.YYYY")}</Text>,
    },
    {
      title: "Done",
      align: "center",
      hidden: user && user.uid !== "C6eD2XCyx5Vi7ANQwqlwdb0Q9Hf2",
      render: (value, record) => (
        <MarkAsDone order={record} doneDate={moment()} onDone={() => markOrderAsDone(record.id)} />
      ),
    },
  ]

  return (
    <>
      <div style={{ marginBottom: 8 }}>
        <ComplaintDrawer
          orderData={selectedOrder}
          disabled={!exactlyOneSelected}
          buttonText="Create Complaint"
        />
      </div>
      <Table
        style={{ marginTop: "15px" }}
        size="small"
        loading={loading}
        columns={defaultColumns}
        bordered
        dataSource={data}
        rowSelection={rowSelection}
        rowKey={(record) => record.id}
        pagination={{ defaultPageSize: "12", showSizeChanger: true }}
      />
    </>
  )
}
